import { query, mutate } from './graphqlService.js'
import axios from '~/util/axios'
const scanRequestQuery = `
  guid
  name
  completed
  status_description
  detected_doc_names
  created_at
  disclosure_package_scan_request_warnings {
    guid
    warning_type
    warning_description
    doc_name
  }
  uploaded_by {
    full_name
  }
`
const scheduleClosingTaskQuery = `
  id
  guid
  completed
  completedAt
  confirmed
  confirmedAt
  closingDate
  closeType
  proposedClosingTime
  settlementAgentAvailability`

const taskDefQuery = `
  task_name
  primary_header
  secondary_header_prefix
  secondary_header_timestamp
  doc_count_text
  printable
  step_defs {
    header
    header_tooltip
    description
    action_label
    confirmation_label
    note
    note_header
  }
`

const signingStatusQuery = `
  assigneeName: assignee_name
  subtitle
  signingWindowNotYetOpen: signing_window_not_yet_open
  traditional
  esignIcon: esign_icon {
    type
    color
    tooltipText: tooltip_text
  }
  ronIcon: ron_icon {
  type
    color
    tooltipText: tooltip_text
  }
  enoteIcon: enote_icon {
  type
    color
    tooltipText: tooltip_text
  }
`

const notarizeTransactionQuery = `
  guid
  current_state
  failed
  recording_jurisdiction_id
  recording_jurisdiction_name
  recording_jurisdiction_state
  title_agency_id
  title_agency_name
  title_agency_street1
  title_agency_street2
  title_agency_city
  title_agency_state
  title_agency_zip
  underwriter_id
  underwriter_name
  require_secondary_id
  localized_ron_window
  ron_window_ends_at
  localized_ron_window_ends_at(format: "%h %-d, %Y at %I:%M %p %Z")
  ron_window_start_date
  ron_window_end_date
  message_to_signer
  message_to_notarize
`

const assignmentQuery = `
  guid
  name
  action_type
  current_state
  status
  expired
  started_at
  started
  completed_at
  action_performed
  disclosure_package_id
  disclosure_setting_doc_viewer_enabled
  disclosure_setting_import_closing_as_one_doc
  disclosure_setting_allow_settlement_agent_to_prepare_ron_docs
  role_type
  scan_requests_in_progress
  closing_portal_task_def {
    ${taskDefQuery}
  }
  disclosure_package_scan_requests(include_proxy_scan_requests: true) {
    ${scanRequestQuery}
  }
  entity {
    __typename
    ... on DisclosureMailSignTrackingEntry {
      tracking_type
      tracking_id
    }
  }
  entity_artifact {
    guid
    docs {
      __typename
      guid
      name
      createdAt
      canRemove
      ... on DisclosureDoc {
        remote_disclosure_tracking_entry {
          guid
        }
      }
    }
  }
  recipient_suggested_docs {
    name
    completed
  }
  notarize_transaction {
    ${notarizeTransactionQuery}
  }
`

const packageQuery = `
  id
  guid
  name
  unread
  created_at
  updated_at
  esign_window_start_date
  esign_window_end_date
  borrower_names_string
  package_type
  closing_portal_file_type
  current_state
  allow_uploads
  needs_closing_day_sync
  has_completed_esign_docs
  has_eclosing_esign_tasks
  can_prepare_eclosing_docs
  prepare_doc_button_text
  merged
  locked_until
  package_status
  disclosure_package_closer {
    guid
    name
    email
  }
  loan {
    id
    guid
    title_file_number
    closing_date
    actual_closing_date
    loan_property {
      street
      city
      state
      zip
    }
  }
  disclosure_docs(combined: false) {
    guid
    name
  }
  inactive_disclosure_docs {
    guid
    name
  }
  missing_doc_names
  disclosure_package_closer {
    full_name
    email
    phone
  }
  servicer_profile {
    full_name
    email
    phone
    header_img_url
    profile_url
    effective_company_name
    street1
    street2
    city
    state
    zip
    require_disclosures_2fa
  }
  disclosure_package_scan_requests {
    ${scanRequestQuery}
  }
  disclosure_assignments (user_assignments: true) {
    ${assignmentQuery}
  }
  disclosure_package_artifact_options {
    label
    value
    doc_count
    warning
  }
  latest_tracking_entry {
    guid
  }
`

const loanQuery = `
  guid
  title_file_number
  loan_number
  closing_date
  closing_expired
  closing_portal_file_type
  actual_closing_date
  company {
    name
  }
  borrower {
    guid
    full_name
  }
  servicer_profile {
    allow_chat_in_closing_portal
    has_contextual_chat
    has_contextual_chat_allowed
  }
  disclosure_packages (order: $packageOrder) {
    guid
    unread
    current_state
    created_at
    package_status
  }
  disclosure_assignments(is_recipient_task: true, user_assignments: true) {
    guid
    completed_at
    completed
  }
  loan_property {
    street
    city
    state
    zip
  }
`

const closingPortalSharedContactQuery = `
    id
    guid
    user_id
    name
    email
    share_count
    manager
    share_all_files
    ignore_from_recommendations
    created_at
    updated_at
    currently_shared_files_count
  `

export default {
  getLoan (loanGuid, triggerLoanAccessEvent = false) {
    const params = `
      query loan($guid: ID, $packageOrder: String, $triggerLoanAccessEvent: Boolean) {
        loan(guid: $guid, triggerLoanAccessEvent: $triggerLoanAccessEvent) {
          id
          guid
          actual_closing_date
          title_file_number
          loan_number
          loan_amount
          closing_date
          closing_expired
          closing_portal_file_type
          status
          package_guid_for_new_trailing_docs
          trailing_docs {
            id
            guid
            name
            image_url
          }
          borrower {
            full_name
            last_name
            guid
            app_user {
              id
              guid
              user {
                id
              }
            }
          }
          co_borrower {
            full_name
            app_user {
              id
              user {
                id
                name
              }
            }
          }
          loan_borrowers {
            app_user {
              id
              user {
                id
              }
            }
            full_name
            first_name
            last_name
            email
            co_borrower
            formatted_main_phone
            street1
            street2
            city
            state
            zip
            pair_index
          }
          loan_property {
            street
            city
            state
            zip
          }
          buyer_agent {
            full_name
            company_name
            email
            phone
            cell_phone
            street
            city
            state
            zip
            partner {
              profile_img_url
            }
          }
          seller_agent {
            full_name
            company_name
            email
            phone
            cell_phone
            street
            city
            state
            zip
            partner {
              profile_img_url
            }
          }
          servicer_profile {
            id
            guid
            full_name
            email
            phone
            header_img_url
            profile_url
            effective_company_name
            allow_sending_attachments
            allow_chat_in_closing_portal
            has_contextual_chat
            has_contextual_chat_allowed
            allow_closing_contributors
            street1
            street2
            city
            state
            zip
            company {
              guid
            }
            branch {
              guid
            }
            region {
              guid
            }
          }
          disclosure_packages(order: $packageOrder) {
            ${packageQuery}
          }
          disclosure_assignments(is_recipient_task: true, user_assignments: true) {
            ${assignmentQuery}
          }
          allow_schedule_closing_task
          schedule_closing_task {
            ${scheduleClosingTaskQuery}
          }
          closing_portal_signing_statuses {
            ${signingStatusQuery}
          }
        }
      }
    `
    const variables = {
      guid: loanGuid,
      packageOrder: 'created_at DESC',
      triggerLoanAccessEvent: triggerLoanAccessEvent
    }
    return query(params, variables, 'no-cache')
  },

  getLoans ({ search, loanGuids, packageStatuses, first, last, before, after, order = 'created_at DESC' }) {
    const params = `
      query loans ($search: String, $loanGuids: [ID!], $packageStatuses: [String!], $search_fields: [String!], $first: Int, $last: Int, $before: String, $after: String, $order: String, $packageOrder: String) {
        loans(search: $search, loan_guids: $loanGuids, disclosure_package_states: $packageStatuses, search_fields: $search_fields, first: $first, last: $last, before: $before, after: $after, order: $order) {
          filteredTotal
          pageInfo {
            startCursor
            endCursor
          }
          nodes {
            ${loanQuery}
          }
        }
      }
      `

    if (!order) {
      order = 'packagesCreated DESC'
    }
    const variables = {
      search,
      loanGuids,
      packageStatuses,
      first,
      last,
      before,
      after,
      order,
      search_fields: ['borrower', 'loanNumber', 'coBorrower', 'titleFileNumber', 'borrowerEmail'],
      packageOrder: 'created_at DESC'
    }
    return query(params, variables, 'network-only')
  },

  getPackage (packageGuid = '') {
    const params = `
      query disclosure_package ($packageGuid: ID!) {
        disclosure_package(package_guid: $packageGuid) {
          ${packageQuery}
        }
      }
      `

    const variables = {
      packageGuid: packageGuid + ''
    }
    return query(params, variables, 'network-only')
  },

  getAssignment (assignmentGuid) {
    const params = `
      query disclosure_assignment ($assignmentGuid: ID!) {
        disclosure_assignment(assignment_guid: $assignmentGuid) {
          ${assignmentQuery}
        }
      }
      `

    const variables = {
      assignmentGuid: assignmentGuid
    }
    return query(params, variables, 'network-only')
  },

  getRecipients (loanGuid) {
    const params = `
    query loan($guid: ID, $packageOrder: String) {
      loan(guid: $guid) {
        disclosure_packages (order: $packageOrder) {
          owner_recipient {
            email
          }
          contributor_recipients {
            email
            can_remove
          }
        }
      }
    }
    `
    const variables = {
      guid: loanGuid,
      packageOrder: 'created_at DESC'
    }
    return query(params, variables, 'network-only')
  },

  grantAccessToLoan (loanGuid, email) {
    const params = `
      mutation grantAccessToLoan ($loanGuid: ID!, $email: String!) {
        grant_access_to_loan (loan_guid: $loanGuid, email: $email) {
          successful
          error
          shared_contact {
            id
            guid
            email
            share_count
            share_all_files
            ignore_from_recommendations
          }
        }
      }
    `

    const variables = { loanGuid, email }
    return mutate(params, variables, 'network-only')
  },

  removeAccessToLoan (loanGuid, email) {
    const params = `
      mutation removeAccessToLoan ($loanGuid: ID!, $email: String!) {
        remove_access_to_loan (loan_guid: $loanGuid, email: $email) {
          successful
          error
        }
      }
    `

    const variables = { loanGuid, email }
    return mutate(params, variables, 'network-only')
  },

  getMyAccountProfileInfo (userGuid) {
    const params = `
      query user ($guid: ID!) {
        user(guid: $guid) {
          id
          name
          last_name
          email
          unformatted_phone
        }
      }
    `
    const variables = {
      guid: userGuid
    }
    return query(params, variables, 'network-only')
  },

  updateMyAccountProfileInfo (firstName, lastName, phone) {
    const params = `
      mutation updateUserMutation($first_name: String, $last_name: String, $phone: String) {
        update_user(first_name: $first_name, last_name: $last_name, phone: $phone) {
          user {
            id
            name
            last_name
            email
            unformatted_phone
          }
        }
      }
    `
    const variables = {
      first_name: firstName,
      last_name: lastName,
      phone: phone
    }
    return mutate(params, variables, 'network-only')
  },

  updatePassword (currentPassword, newPassword, confirmPassword) {
    const params = `
      mutation updatePasswordMutation($current_password: String!, $new_password: String!, $confirm_password: String!) {
        update_password_mutation(current_password: $current_password, new_password: $new_password, new_password_confirmation: $confirm_password) {
          message
        }
      }
    `
    const variables = {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword
    }
    return mutate(params, variables, 'network-only')
  },

  getSharedContacts () {
    const params = `
      query closingPortalSharedContacts {
        closing_portal_shared_contacts {
          ${closingPortalSharedContactQuery}
        }
      }
    `
    const variables = {}
    return query(params, variables, 'no-cache')
  },

  createSharedContact (name, email, isManager) {
    const params = `
      mutation createClosingPortalSharedContact($sharedContact: CreateClosingPortalSharedContactType!) {
        create_closing_portal_shared_contact(shared_contact: $sharedContact) {
          successful
          error
          closing_portal_shared_contact {
           ${closingPortalSharedContactQuery}
          }
        }
      }
    `

    const variables = { sharedContact: { name: name, email: email, manager: isManager } }
    return mutate(params, variables, 'network-only')
  },

  sharedContactIgnoreRecommendations (sharedContactId, value) {
    const params = `
      mutation ignoreRecommendations($sharedContactId: ID!, $value: Boolean!) {
        closing_portal_shared_contact_ignore_recommendations(shared_contact_id: $sharedContactId, value: $value) {
            successful
          }
        }
    `
    const variables = { sharedContactId: sharedContactId, value: value }
    return mutate(params, variables, 'network-only')
  },

  updateClosingPortalSharedContactPreferences (contactGuid, shareAllFlag, name, email, manager) {
    const params = `
      mutation UpdateClosingPortalSharedContactPreferences($contactGuid: String!, $shareAllFlag: Boolean, $name: String, $email: String, $manager: Boolean) {
        update_closing_portal_shared_contact_preferences(contact_guid: $contactGuid, share_all_files: $shareAllFlag, name: $name, email: $email, manager: $manager) {
          successful
          error
          closing_portal_shared_contact {
           ${closingPortalSharedContactQuery}
          }
        }
      }
    `

    const variables = { contactGuid, shareAllFlag, name, email, manager }
    return mutate(params, variables, 'network-only')
  },

  deleteSharedContact (guid) {
    const params = `
      mutation deleteClosingPortalSharedContact($sharedContactGuid: ID!) {
        delete_closing_portal_shared_contact(shared_contact_guid: $sharedContactGuid) {
          successful
          error
        }
      }
    `

    const variables = { sharedContactGuid: guid }
    return mutate(params, variables, 'network-only')
  },

  uploadFiles (packageGuid, assignmentGuid, files, newDoc = false, trailingDocs = false) {
    const data = new FormData()

    data.append('origin', 'website')
    data.append('user_agent', navigator.userAgent)

    files.forEach((file) => {
      data.append('files[]', file)
      data.append('names[]', file.name)
    })

    return axios.post(`/recipient/upload?package_guid=${packageGuid}&assignment_guid=${assignmentGuid}&new_doc=${newDoc}&trailing_docs=${trailingDocs}`, data)
  },

  getEditRedirectUrl (packageGuid, currentPage) {
    const params = `
      mutation generate_eclose_url ($packageGuid: ID!, $currentPage: String!) {
        generate_eclose_url(package_guid: $packageGuid, current_page: $currentPage) {
          url
          error
        }
      }
    `
    const variables = {
      packageGuid: packageGuid,
      currentPage: currentPage
    }
    return mutate(params, variables, 'network-only')
  },

  getAuthenticationAttempts () {
    const params = `
      query authenticationAttempts {
        authentication_attempts_user {
          guid
          created_at
          location {
            ip
            city
            state
          }
          current_state
        }
      }
    `

    const variables = {}

    return query(params, variables, 'network-only')
  },

  releaseForEclose (assignmentGuid) {
    const params = `
      mutation release_for_eclose ($guid: ID!) {
        release_for_eclose(guid: $guid) {
          disclosure_assignment {
            ${assignmentQuery}
          }
          msg
        }
      }
    `
    const variables = {
      guid: assignmentGuid
    }
    return mutate(params, variables, 'network-only')
  },

  createScheduleClosingTask (
    servicerProfileGuid,
    loanGuid,
    companyGuid,
    regionGuid,
    branchGuid,
    ronEligible,
    onlineEligible,
    hybridEligible,
    closingDate,
    settlementAgentAvailability,
    appUserGuid) {
    const params = `
    mutation create_schedule_closing_task (
      $servicerProfileGuid: ID!,
      $loanGuid: ID,
      $companyGuid: ID,
      $regionGuid: ID,
      $branchGuid: ID,
      $ronEligible:Boolean!,
      $onlineEligible:Boolean!,
      $hybridEligible:Boolean!,
      $closingDate: ISO8601DateTime,
      $settlementAgentAvailability: String,
      $appUserGuid: ID) {
      createScheduleClosingTask(
        servicerProfileGuid: $servicerProfileGuid,
        loanGuid: $loanGuid,
        companyGuid: $companyGuid,
        regionGuid: $regionGuid,
        branchGuid:$branchGuid,
        ronEligible: $ronEligible,
        onlineEligible:$onlineEligible,
        hybridEligible:$hybridEligible,
        closingDate: $closingDate,
        settlementAgentAvailability: $settlementAgentAvailability,
        appUserGuid: $appUserGuid)
        {
          scheduleClosingTask{
            ${scheduleClosingTaskQuery}
          }
        }
    }
    `
    const variables = {
      servicerProfileGuid: servicerProfileGuid,
      loanGuid: loanGuid,
      regionGuid: regionGuid,
      branchGuid: branchGuid,
      ronEligible: ronEligible,
      onlineEligible: onlineEligible,
      hybridEligible: hybridEligible,
      closingDate: closingDate,
      companyGuid: companyGuid,
      settlementAgentAvailability: settlementAgentAvailability,
      appUserGuid: appUserGuid
    }
    return mutate(params, variables, 'network-only')
  },

  confirmScheduleClosingTask (taskGuid, servicerProfileId, proposedClosingTime, closingDate) {
    const params = `
      mutation confirmScheduleClosingTask($guid: ID!, $proposedClosingTime: ISO8601DateTime!, $servicerProfileId: ID, $closingDate: String!) {
        updateScheduleClosingTask(servicerProfileId: $servicerProfileId, guid: $guid, proposedClosingTime: $proposedClosingTime, closingDate: $closingDate, confirmed: true) {
          success
        }
      }
    `
    const variables = {
      guid: taskGuid,
      proposedClosingTime: proposedClosingTime,
      closingDate: closingDate,
      servicerProfileId: servicerProfileId
    }
    return mutate(params, variables, 'network-only')
  },

  updateScheduleClosingTask (taskGuid, servicerProfileId, settlementAgentAvailability, closingDate) {
    const params = `
      mutation updateScheduleClosingTask($guid: ID!, $servicerProfileId: ID, $settlementAgentAvailability: String, $closingDate: String!) {
        updateScheduleClosingTask(guid: $guid, servicerProfileId: $servicerProfileId,  settlementAgentAvailability: $settlementAgentAvailability, closingDate: $closingDate) {
          success
        }
      }
    `
    const variables = {
      guid: taskGuid,
      settlementAgentAvailability: settlementAgentAvailability,
      closingDate: closingDate,
      servicerProfileId: servicerProfileId
    }
    return mutate(params, variables, 'network-only')
  },

  getScheduleClosingTask (taskGuid) {
    const params = `
      query schedule_closing_task ($guid: ID!) {
        scheduleClosingTask(guid: $guid) {
          ${scheduleClosingTaskQuery}
        }
      }
      `
    const variables = {
      guid: taskGuid
    }
    return query(params, variables, 'network-only')
  },

  notifyBorrowerOfClosingPackageUpdates (packageGuid, customMessage) {
    const params = `
      mutation notify_borrower_of_closing_package_updates ($packageGuid: ID!, $customMessage: String, $roleType: String) {
        notify_borrower_of_closing_package_updates (package_guid: $packageGuid, custom_message: $customMessage, role_type: $roleType) {
          guid
          error
        }
      }
    `
    const variables = {
      packageGuid: packageGuid,
      customMessage: customMessage,
      roleType: 'Settlement Agent'
    }
    return mutate(params, variables, 'network-only')
  },

  progressPackageToEclose (packageGuid) {
    const params = `
      mutation set_package_ready_to_eclose($guid: ID!) {
        set_package_ready_to_eclose(package_guid: $guid) {
          package_guid
          error
        }
      }
    `

    const variables = {
      guid: packageGuid
    }
    return mutate(params, variables, 'network-only')
  },

  getPackageArtifact (packageGuid, { guid, fileType, docTypes }) {
    const params = `
      query disclosure_package ($packageGuid: ID!, $guid: ID, $fileType: String!, $docTypes: [String!]!) {
        disclosure_package(package_guid: $packageGuid) {
          guid
          disclosure_package_artifact(guid: $guid, file_type: $fileType, doc_types: $docTypes) {
            guid
            complete
            failed
            file_url
          }
        }
      }
    `

    const variables = {
      packageGuid, fileType, docTypes
    }
    if (guid) {
      variables.guid = guid
    }
    return query(params, variables, 'network-only')
  },

  getChannelInfoForFiles (loanGuids, unread = false) {
    const params = `
      query getChannels ($first: Int!, $loanGuids: [ID!], $unread: Boolean) {
        channels(first: $first, loanGuids: $loanGuids, unread: $unread) {
          edges {
            cursor
            node {
              guid
              contextGuid
              unreadMessageCountForUser
            }
          }
        }
      }
    `
    var variables = {
      first: loanGuids.length > 0 ? loanGuids.length * 2 : 50,
      loanGuids: loanGuids
    }
    if (unread) variables.unread = true

    return query(params, variables, 'network-only')
  },

  updateNotarizeTransaction (taskGuid, updates) {
    const params = `
      mutation update_notarize_transaction (
          $task_guid: ID!,
          $require_secondary_id: Boolean,
          $message_to_signer: String,
          $message_to_notarize: String
        ) {
        update_notarize_transaction(
          task_guid: $task_guid,
          require_secondary_id: $require_secondary_id,
          message_to_signer: $message_to_signer,
          message_to_notarize: $message_to_notarize
        ) {
          notarize_transaction {
            ${notarizeTransactionQuery}
          }
        }
      }
    `
    const variables = {
      task_guid: taskGuid,
      ...updates
    }
    return mutate(params, variables, 'network-only')
  },

  submitNotarizeTransaction (taskGuid) {
    const params = `
    mutation submit_notarize_transaction($task_guid: ID!) {
      submit_notarize_transaction(task_guid: $task_guid) {
        notarize_transaction {
          ${notarizeTransactionQuery}
        }
      }
    }
    `
    const variables = {
      task_guid: taskGuid
    }
    return mutate(params, variables, 'network-only')
  },

  confirmDocsMailed (taskGuid, trackingType, trackingID) {
    const params = `
    mutation confirm_docs_mailed($guid: ID!, $tracking_type: String!, $tracking_id: String!) {
      confirm_docs_mailed(guid: $guid, tracking_type: $tracking_type, tracking_id: $tracking_id) {
        disclosure_assignment {
          ${assignmentQuery}
        }
        msg
      }
    }
    `
    const variables = {
      guid: taskGuid,
      tracking_type: trackingType,
      tracking_id: trackingID
    }
    return mutate(params, variables, 'network-only')
  },

  dismissTask (taskGuid) {
    const params = `
      mutation deactivate_disclosure_assignment ($guid: ID!) {
        deactivate_disclosure_assignment(guid: $guid) {
          msg
        }
      }
    `
    const variables = {
      guid: taskGuid
    }
    return mutate(params, variables, 'network-only')
  }
}
